import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import classnames from 'classnames/bind';

import BaseModal from '@MODALS/BaseModal';
import BasicInput from '@COMPONENTS/COMMON/inputs/input/BasicInput';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import FileUploadDropzone from '@COMPONENTS/COMMON/inputs/file/FileUploadDropzone';

import styles from './EntityFormModal.module.scss';

const cx: CX = classnames.bind(styles);

interface FormField {
    key: string;
    label: string;
    type: 'text' | 'select' | 'multiselect' | 'file';
    required?: boolean;
    options?: { value: string | number; label: string }[];
    placeholder?: string;
    accept?: string; // For file inputs
    helpText?: string;
    validation?: {
        pattern?: {
            value: RegExp;
            message: string;
        };
        minLength?: {
            value: number;
            message: string;
        };
        maxLength?: {
            value: number;
            message: string;
        };
    };
}

interface EntityFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    title: string;
    fields: FormField[];
    initialData?: any;
    isLoading?: boolean;
}

function EntityFormModal(props: EntityFormModalProps) {
    const {
        isOpen, onClose, onSubmit, title, fields, initialData, isLoading = false,
    } = props;

    const [formData, setFormData] = useState<any>({});
    const [fileData, setFileData] = useState<{ [key: string]: File | null }>({});
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Initialize form data when modal opens or initialData changes
    useEffect(() => {
        if (isOpen) {
            const newFormData: any = {};
            const newFileData: { [key: string]: File | null } = {};

            fields.forEach((field) => {
                if (field.type === 'file') {
                    newFileData[field.key] = null;
                } else if (field.type === 'multiselect') {
                    newFormData[field.key] = initialData?.[field.key] ?? [];
                } else {
                    newFormData[field.key] = initialData?.[field.key] ?? '';
                }
            });

            setFormData(newFormData);
            setFileData(newFileData);
            setErrors({});
        }
    }, [isOpen, initialData, fields]);

    const handleInputChange = (field: string, value: string) => {
        setFormData((prev: any) => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const handleMultiSelectChange = (field: string, value: any[]) => {
        setFormData((prev: any) => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user makes a selection
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const handleFileChange = (field: string, file: File | null) => {
        setFileData((prev) => ({
            ...prev,
            [field]: file,
        }));

        // Clear error when user selects a file
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        fields.forEach((field) => {
            if (field.required) {
                if (field.type === 'file') {
                    if (!fileData[field.key] && !initialData?.[field.key]) {
                        newErrors[field.key] = `${field.label} is required`;
                    }
                } else if (field.type === 'multiselect') {
                    if (!formData[field.key] || formData[field.key].length === 0) {
                        newErrors[field.key] = `${field.label} is required`;
                    }
                } else if (!formData[field.key] || (typeof formData[field.key] === 'string' && formData[field.key].trim() === '')) {
                    newErrors[field.key] = `${field.label} is required`;
                }
            }

            // Pattern validation
            if (field.validation?.pattern) {
                const value = formData[field.key];
                if (value && !field.validation.pattern.value.test(value)) {
                    newErrors[field.key] = field.validation.pattern.message;
                }
            }

            // Min length validation
            if (field.validation?.minLength) {
                const value = formData[field.key];
                if (value && value.length < field.validation.minLength.value) {
                    newErrors[field.key] = field.validation.minLength.message;
                }
            }

            // Max length validation
            if (field.validation?.maxLength) {
                const value = formData[field.key];
                if (value && value.length > field.validation.maxLength.value) {
                    newErrors[field.key] = field.validation.maxLength.message;
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }

        // Combine form data and file data
        const submitData = { ...formData };

        // Add files to the submit data
        Object.entries(fileData).forEach(([key, file]) => {
            if (file) {
                submitData[key] = file;
            }
        });

        onSubmit(submitData);
    };

    const handleClose = () => {
        setFormData({});
        setFileData({});
        setErrors({});
        onClose();
    };

    if (!isOpen) {
        return null;
    }

    return (
        <BaseModal onClose={handleClose}>
            <div className={cx('entity-form-modal')}>
                <div className={cx('modal-header')}>
                    <h2>{title}</h2>
                </div>

                <div className={cx('form-content')}>
                    {fields.map((field) => {
                        const renderField = () => {
                            if (field.type === 'text') {
                                return (
                                    <div className={cx('input-field')}>
                                        <label htmlFor={`field-${field.key}`} className={cx('field-label')}>
                                            {field.label}
                                            {field.required && <span className={cx('required')}>*</span>}
                                        </label>
                                        <input
                                            id={`field-${field.key}`}
                                            type="text"
                                            value={formData[field.key] || ''}
                                            onChange={(e) => handleInputChange(field.key, e.target.value)}
                                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                                            className={cx('text-input', { error: errors[field.key] })}
                                        />
                                        {field.helpText && (
                                            <small className={cx('help-text')}>{field.helpText}</small>
                                        )}
                                        {errors[field.key] && (
                                            <span className={cx('error-message')}>{errors[field.key]}</span>
                                        )}
                                    </div>
                                );
                            }

                            if (field.type === 'select') {
                                return (
                                    <div className={cx('select-field')}>
                                        <label htmlFor={`field-${field.key}`} className={cx('field-label')}>
                                            {field.label}
                                            {field.required && <span className={cx('required')}>*</span>}
                                        </label>
                                        <select
                                            id={`field-${field.key}`}
                                            value={formData[field.key] || ''}
                                            onChange={(e) => handleInputChange(field.key, e.target.value)}
                                            className={cx('select-input', { error: errors[field.key] })}
                                        >
                                            <option value="">
                                                Select
                                                {' '}
                                                {field.label.toLowerCase()}
                                            </option>
                                            {field.options?.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        {errors[field.key] && (
                                            <span className={cx('error-message')}>{errors[field.key]}</span>
                                        )}
                                    </div>
                                );
                            }

                            if (field.type === 'multiselect') {
                                return (
                                    <div className={cx('select-field')}>
                                        <label htmlFor={`field-${field.key}`} className={cx('field-label')}>
                                            {field.label}
                                            {field.required && <span className={cx('required')}>*</span>}
                                        </label>
                                        <select
                                            id={`field-${field.key}`}
                                            multiple
                                            value={formData[field.key] || []}
                                            onChange={(e) => {
                                                const selectedValues = Array.from(
                                                    e.target.selectedOptions,
                                                    (option) => (Number.isNaN(Number(option.value)) ? option.value : Number(option.value)),
                                                );
                                                handleMultiSelectChange(field.key, selectedValues);
                                            }}
                                            className={cx('select-input', 'multiselect-input', {
                                                error: errors[field.key],
                                            })}
                                            style={{
                                                height: 'auto',
                                                minHeight: '80px',
                                            }}
                                        >
                                            {field.options?.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                        <small style={{
                                            fontSize: '10px',
                                            color: '#6B7280',
                                            marginTop: '4px',
                                            display: 'block',
                                        }}
                                        >
                                            Hold Ctrl (Cmd on Mac) to select multiple options
                                        </small>
                                        {errors[field.key] && (
                                            <span className={cx('error-message')}>{errors[field.key]}</span>
                                        )}
                                    </div>
                                );
                            }

                            if (field.type === 'file') {
                                return (
                                    <div className={cx('file-field')}>
                                        <FileUploadDropzone
                                            label={field.label}
                                            onDrop={(files: File[]) => handleFileChange(field.key, files[0] || null)}
                                            onClear={() => handleFileChange(field.key, null)}
                                            selectedFile={fileData[field.key]}
                                            placeholder={field.placeholder}
                                            required={field.required}
                                            accept={{
                                                'image/svg+xml': [],
                                            }}
                                            maxSize={3 * 1024 * 1024}
                                        />
                                        {errors[field.key] && (
                                            <span className={cx('error-message')}>{errors[field.key]}</span>
                                        )}
                                    </div>
                                );
                            }

                            return null;
                        };

                        return (
                            <div key={field.key} className={cx('form-group')}>
                                {renderField()}
                            </div>
                        );
                    })}
                </div>

                <div className={cx('form-actions')}>
                    <BasicButton
                        title="Cancel"
                        onClick={handleClose}
                        style={{
                            backgroundColor: 'transparent',
                            color: '#6B7280',
                            border: '1px solid #D1D5DB',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: 600,
                            marginRight: '12px',
                        }}
                    />
                    <BasicButton
                        title={initialData ? 'Update' : 'Create'}
                        onClick={handleSubmit}
                        isProcessing={isLoading}
                        style={{
                            backgroundColor: '#1DB97C',
                            color: 'white',
                            padding: '8px 16px',
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                    />
                </div>
            </div>
        </BaseModal>
    );
}

// PropTypes for runtime validation (required for legacy code compatibility)
EntityFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.oneOf(['text', 'select', 'multiselect', 'file']).isRequired,
        required: PropTypes.bool,
        options: PropTypes.arrayOf(PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
        })),
        placeholder: PropTypes.string,
        accept: PropTypes.string,
    })).isRequired,
    initialData: PropTypes.shape({}),
    isLoading: PropTypes.bool,
};

EntityFormModal.defaultProps = {
    initialData: undefined,
    isLoading: false,
};

export default EntityFormModal;
